/**
 * Premium /about page content — company story, history, team, proof.
 */

export const HERO_CAPABILITY_NODES = [
  'Strategy',
  'UX/UI',
  'Frontend',
  'Backend',
  'Ecommerce',
  'AI',
  'CRM/ERP',
  'Support',
];

export const TRUST_METRICS = [
  {
    id: 'global',
    value: 'Global',
    label: 'delivery reach',
    detail: 'Remote-friendly projects for teams across regions and time zones.',
  },
  {
    id: 'fullstack',
    value: 'Full-stack',
    label: 'technical team',
    detail: 'Design, frontend, backend, integrations, and post-launch support.',
  },
  {
    id: 'production',
    value: 'Production',
    label: 'engineering focus',
    detail: 'Performance, security, maintainability, and integration QA.',
  },
  {
    id: 'support',
    value: 'Long-term',
    label: 'partnership model',
    detail: 'Optimization, fixes, roadmap work, and ongoing maintenance.',
  },
];

export const COMPANY_TIMELINE = [
  {
    id: 'roots',
    phase: 'Foundation',
    title: 'Started with web design and custom websites',
    desc: 'Built conversion-focused business websites with clear UX, responsive layouts, and reliable delivery for early clients.',
  },
  {
    id: 'ecommerce',
    phase: 'Expansion',
    title: 'Expanded into ecommerce, Magento, WordPress, and CMS platforms',
    desc: 'Added storefront engineering, Magento and WooCommerce workflows, and WordPress/Roots Sage CMS builds.',
  },
  {
    id: 'backend',
    phase: 'Systems',
    title: 'Built stronger backend, API, CRM, ERP, and operations workflows',
    desc: 'Connected CRM, forms, payments, inventory, and operational systems with secure APIs and automation.',
  },
  {
    id: 'modern',
    phase: 'Innovation',
    title: 'Added AI automation, chatbots, analytics, search, and product engineering',
    desc: 'Modern React/Next.js products, AI assistants, GTM analytics, education search, and integration-heavy delivery.',
  },
  {
    id: 'today',
    phase: 'Today',
    title: 'A senior technical partner for production-ready web systems',
    desc: 'From Gautam Buddha Nagar, India—we support global clients with dependable engineering, honest recommendations, and long-term support.',
  },
];

export type TeamCapability = {
  id: string;
  title: string;
  desc: string;
  iconKey: string;
};

export const TEAM_CAPABILITIES: TeamCapability[] = [
  {
    id: 'architecture',
    title: 'Solution architecture & planning',
    desc: 'Technical roadmaps, scope definition, integration maps, and production-ready architecture.',
    iconKey: 'diagram',
  },
  {
    id: 'design',
    title: 'UI/UX & responsive design',
    desc: 'Conversion-focused interfaces, design systems, and accessible responsive layouts.',
    iconKey: 'palette',
  },
  {
    id: 'frontend',
    title: 'React, Next.js & frontend engineering',
    desc: 'Component-driven UI, performance tuning, and modern frontend architecture.',
    iconKey: 'code',
  },
  {
    id: 'backend',
    title: 'Python, Node.js, PHP/Laravel & APIs',
    desc: 'Secure backends, REST APIs, microservices, queues, and data workflows.',
    iconKey: 'server',
  },
  {
    id: 'commerce',
    title: 'Magento, WooCommerce & WordPress Sage',
    desc: 'Ecommerce storefronts, checkout flows, CMS platforms, and content architecture.',
    iconKey: 'cart',
  },
  {
    id: 'integrations',
    title: 'CRM, ERP, forms, analytics & AI',
    desc: 'Salesforce, Marketo, FormAssembly, GTM, search, chatbots, and workflow automation.',
    iconKey: 'plug',
  },
  {
    id: 'qa',
    title: 'QA, performance, security & support',
    desc: 'Core Web Vitals, monitoring, hardening, fixes, and continuous improvement after launch.',
    iconKey: 'shield',
  },
];

export const DELIVERY_FLOW = [
  {
    id: 'understand',
    step: '01',
    title: 'Understand',
    desc: 'Business goals, users, blockers, and existing systems.',
  },
  {
    id: 'plan',
    step: '02',
    title: 'Plan',
    desc: 'Architecture, scope, milestones, and integration requirements.',
  },
  {
    id: 'design',
    step: '03',
    title: 'Design',
    desc: 'UX, UI, content structure, and conversion paths.',
  },
  {
    id: 'build',
    step: '04',
    title: 'Build',
    desc: 'Frontend, backend, CMS, ecommerce, and AI workflows.',
  },
  {
    id: 'integrate',
    step: '05',
    title: 'Integrate',
    desc: 'CRM, ERP, payments, analytics, forms, and search.',
  },
  {
    id: 'improve',
    step: '06',
    title: 'Improve',
    desc: 'Performance, security, roadmap, and ongoing support.',
  },
];

export const OPERATING_PRINCIPLES = [
  {
    id: 'communication',
    title: 'Clear communication',
    text: 'We document scope, share progress honestly, and explain technical trade-offs in plain language.',
  },
  {
    id: 'maintainable',
    title: 'Maintainable code',
    text: 'We build reusable components, clean APIs, and systems your team can extend after launch.',
  },
  {
    id: 'honest',
    title: 'Honest recommendations',
    text: 'We recommend the right stack and scope—not oversold features or unnecessary complexity.',
  },
  {
    id: 'integration',
    title: 'Integration-first thinking',
    text: 'We map CRM, ERP, payments, analytics, and operations before writing production code.',
  },
  {
    id: 'performance',
    title: 'Performance & security mindset',
    text: 'We test integrations, monitor production behavior, and harden systems for real traffic.',
  },
  {
    id: 'partnership',
    title: 'Long-term partnership',
    text: 'We support optimization, fixes, and roadmap work—not just one-off delivery.',
  },
];

export const ABOUT_TESTIMONIALS = [
  {
    id: 'richard-d',
    quote:
      'Kailash has worked with me on various projects and has always exceeded my expectations. Work always completed in a timely manner. Professional work, and very communicative. I highly recommend Kailash for your future project work.',
    name: 'Richard D.',
    company: 'Tipster Challenge',
    initials: 'RD',
  },
  {
    id: 'mayank-kumar',
    quote:
      'Kailash has demonstrated extraordinary brilliance while working with eVanik Networks. His knack for understanding the problem and solving it with out of the box thinking is what is really expected out of great engineers. I wish him all the best in his future endeavours.',
    name: 'Mayank Kumar',
    company: 'eVanik Networks',
    initials: 'MK',
  },
];

export const ABOUT_CLIENT_LOGOS = [
  {
    href: 'https://web.kailashchandra.com/tosall_client/',
    src: 'img/tosall-logo-plux.png',
    alt: 'Tosall logo',
  },
  {
    href: 'https://tipsterchallenge.com/',
    src: 'img/logo-tipster.jpg',
    alt: 'Tipster Challenge logo',
  },
  {
    href: 'https://www.wecharge.co.in/',
    src: 'img/logo-wechage.png',
    alt: 'WeCharge logo',
  },
  {
    href: 'https://web.kailashchandra.com/pscs/front/',
    src: 'img/logo-pscs.png',
    alt: 'PSCS logo',
    className: 'hover u-bg-navy',
  },
  {
    href: 'https://surveys.authenticopinion.com/clientpanel',
    src: 'img/logo-trueopinion.png',
    alt: 'True Opinion logo',
  },
  {
    href: 'https://web.kailashchandra.com/cashewprotect/',
    src: 'img/logo-cashew.png',
    alt: 'Cashew logo',
  },
];

export const TEAM_POSITIONING =
  'Our strength is not only building pages. We connect design, engineering, integrations, and support so clients get systems they can operate and improve.';
